import Alert from "@mui/joy/Alert";
import Box from "@mui/joy/Box";
import Checkbox from "@mui/joy/Checkbox";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Link from "@mui/joy/Link";
import Stack from "@mui/joy/Stack";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import AppCTA from "@components/AppCTA/AppCTA";
import AppPasswordInput from "@components/AppPasswordInput/AppPasswordInput";
import GeneralConst from "@consts/GeneralConst";
import RoutesConst from "@consts/RoutesConst";
import UserService from "@services/UserService";
import { login } from "@slices/userSlice.js";
import AlertStrings from "@strings/AlertStrings.json";
import GeneralStrings from "@strings/GeneralStrings.json";
import { validateEmail, validatePassword } from "@utilities/ValidationHelper";
import LoginLayout from "@pages/LoginPage/LoginLayout";

interface SignInFormProps {
  setEmail: (email: string) => void;
  setTempPassword: (tempPassword: string) => void;
  setFormType: (formType: string) => void;
}

const SignInForm = ({
  setEmail,
  setTempPassword,
  setFormType,
}: SignInFormProps) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [errorMessage, setErrorMessage] = useState(null);
  const [formStatus, setFormStatus] = useState<null | string>(null);
  const STATUS_CODES = GeneralConst.STATUS_CODES;

  const resetErrorMessages = () => {
    setErrorMessage(null);
    setFormStatus(null);
  };

  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  ): void => {
    event.preventDefault();
    setFormType(GeneralConst.LOGIN_FORM_TYPE.FORGOT_PASSWORD);
  };

  const handleSubmit = async (formValues: {
    email: string;
    password: string;
  }) => {
    resetErrorMessages();

    try {
      const response = await UserService.login(formValues);
      const status = response.data.status;
      if (status === STATUS_CODES.ERROR) {
        setErrorMessage(
          response?.data?.statusMessage || AlertStrings.generalError,
        );
        setFormStatus(STATUS_CODES.ERROR);
      } else if (status === STATUS_CODES.PARTIAL_SUCCESS) {
        setEmail(formValues.email);
        setTempPassword(formValues.password);
        setFormType(GeneralConst.LOGIN_FORM_TYPE.RESET_PASSWORD);
      } else {
        dispatch(
          login({
            email: formValues.email,
          }),
        );
        navigate(RoutesConst.DASHBOARD.DEFAULT);
      }
    } catch (response: any) {
      setErrorMessage(response?.message || AlertStrings.generalError);
      setFormStatus(STATUS_CODES.ERROR);
    }
  };

  return (
    <LoginLayout>
      <Stack gap={4} sx={{ mb: 2 }}>
        <Stack gap={1}>
          <h3>{GeneralStrings.form.signIn}</h3>
        </Stack>
      </Stack>

      {formStatus === STATUS_CODES.ERROR && (
        <Alert
          startDecorator={<i className="fa-solid fa-circle-exclamation" />}
          variant="solid"
          color="danger"
          size="lg"
        >
          {errorMessage}
        </Alert>
      )}

      <Stack gap={4} sx={{ mt: 2 }}>
        <Formik
          initialValues={{
            email: "",
            password: "",
          }}
          onSubmit={handleSubmit}
        >
          {({ submitForm }) => (
            <Form>
              <FormControl required>
                <FormLabel>{GeneralStrings.form.email}</FormLabel>
                <Field
                  name="email"
                  className="form-control"
                  validate={validateEmail}
                  type="email"
                  data-testid="email-input"
                />
                <ErrorMessage
                  component="span"
                  className="form-error"
                  name="email"
                />
              </FormControl>
              <FormControl required>
                <FormLabel>{GeneralStrings.form.password}</FormLabel>
                <Field
                  as={AppPasswordInput}
                  name="password"
                  className="form-control"
                  validate={validatePassword}
                  data-testid="password-input"
                />
                <ErrorMessage
                  component="span"
                  className="form-error"
                  name="password"
                />
              </FormControl>
              <Stack gap={4} sx={{ mt: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Checkbox size="sm" label="Remember me" name="persistent" />
                  <Link
                    component="button"
                    type="button"
                    className="forgot-password-link"
                    onClick={(event) => handleClick(event)}
                  >
                    {GeneralStrings.form.forgotYourPassword}
                  </Link>
                </Box>
                <AppCTA
                  type="button"
                  variant="primary"
                  fullWidth
                  label={GeneralStrings.form.signIn}
                  onClick={async () => {
                    await submitForm();
                  }}
                />
              </Stack>
            </Form>
          )}
        </Formik>
      </Stack>
    </LoginLayout>
  );
};

export default SignInForm;
